import { scan } from 'react-scan' // must be imported before React and React DOM
import { GoogleOAuthProvider } from '@react-oauth/google'
import * as Sentry from '@sentry/react'
import { RouterProvider, createRouter } from '@tanstack/react-router'
import { StrictMode } from 'react'
import ReactDOM from 'react-dom/client'

import { injectRouterToFetchInterceptors } from '@/api/apiInterceptors'
import { routeTree } from '@/app/routes.gen'
import { basePathInfo } from '@/constants/config.constants'
import { credentials } from '@/constants/credentials.constants'
import { getEnv } from '@/libs/runtime-env/runtimeEnv'
import {
  TanStackQueryProvider,
  getTanStackQueryContext,
} from '@/libs/tanstack-query/TanStackQueryProvider'
import { setupSentry } from './libs/sentry/setupSentry'
import reportWebVitals from './reportWebVitals'
import './styles/global.css'

if (getEnv('VITE_APP_REACT_SCAN_DEVTOOLS') === 'true') {
  scan()
}

// Create a new router instance
const router = createRouter({
  routeTree,
  basepath: basePathInfo || '/',
  context: {
    ...getTanStackQueryContext(),
  },
  defaultPreload: 'intent',
  scrollRestoration: true,
  defaultStructuralSharing: true,
  defaultPreloadStaleTime: 0,
})

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
  interface HistoryState {
    redirectTo?: {
      pathname: string
      search: string
    }
  }
}

// setup fetch interceptors
injectRouterToFetchInterceptors(router)

// setup sentry
setupSentry(router)

// Start with mocked endpoints
async function prepareWorkers(): Promise<void> {
  if (getEnv('VITE_APP_USE_MSW_MOCKS') === 'true') {
    const { worker } = await import('./api/apiMocks/browser')

    await worker.start({
      onUnhandledRequest: 'bypass',
      serviceWorker: {
        url: `${import.meta.env.BASE_URL}mockServiceWorker.js`,
      },
    })
  }
}

prepareWorkers().then(() => {
  // Render the app
  const rootElement = document.getElementById('app')

  if (rootElement && !rootElement.innerHTML) {
    const root = ReactDOM.createRoot(rootElement, {
      onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
        // oxlint-disable-next-line no-console
        console.warn('Uncaught error', error, errorInfo.componentStack)
      }),
      onCaughtError: Sentry.reactErrorHandler(),
      onRecoverableError: Sentry.reactErrorHandler(),
    })

    root.render(
      <StrictMode>
        <GoogleOAuthProvider clientId={credentials.googleClientId}>
          <TanStackQueryProvider>
            <RouterProvider router={router} />
          </TanStackQueryProvider>
        </GoogleOAuthProvider>
      </StrictMode>,
    )
  }

  // If you want to start measuring performance in your app, pass a function
  // to log results (for example: reportWebVitals(console.log))
  // or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
  reportWebVitals()
})
